import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import EditIcon from '@mui/icons-material/Edit'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { orderService, adminOrderService } from '../services'
import RejectOrderModal from './RejectOrderModal'
import ModifyAcceptOrderModal from './ModifyAcceptOrderModal'
import CreateShipmentModal from './CreateShipmentModal'
import CompleteOrderDialog from './CompleteOrderDialog'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, ADMIN_ACTIONS } from '../utils/adminPermissions'
import { parseInsufficientStockAcceptError } from '../utils/orderAcceptErrors'
import {
  getOrderPayableAmount,
  hasOrderPaymentProofUploaded,
  isOrderPaymentSatisfiedForProcurement,
  isZeroPayableOrder,
} from '../utils/paymentStatusDisplay'
import { wouldCompleteAsPartialDelivery } from '../utils/ordersListUtils'

const OrderActionsPanel = ({
  order,
  onOrderUpdated,
  onProductDsdUpdated,
  onProductVatUpdated,
  canEnterManualSo = false,
}) => {
  // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
  // Org flag from GET /admin/orders/:id. After rollout, always pass true / remove prop.
  const externalIntegrationEnabled =
    order?.shipment_external_integration_enabled === true
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const canModifyOrder =
    hasAdminPermission(adminPermissions, 'order', 'write') ||
    hasAdminPermission(adminPermissions, 'order', 'create')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [stockShortageItems, setStockShortageItems] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [lazyRemainingPayment, setLazyRemainingPayment] = useState(null)

  useEffect(() => {
    if (pendingAction !== 'startProcurement' || !order?.order_id) return undefined
    if (order?.payment_proof_detail?.summary?.remaining != null) return undefined
    let cancelled = false
    orderService
      .getOrderPayment(order.order_id)
      .then((res) => {
        if (!cancelled) {
          setLazyRemainingPayment(res?.payment_proof_detail?.summary?.remaining ?? null)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [pendingAction, order])
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [modifyModalOpen, setModifyModalOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)
  const [shipmentModalOpen, setShipmentModalOpen] = useState(false)
  const [skipPaymentProofDialogOpen, setSkipPaymentProofDialogOpen] = useState(false)
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false)

  // Determine available actions based on order_status
  const getAvailableActions = () => {
    if (!order || !order.order_status) return []

    const status = order.order_status.toUpperCase()
    // Hard lock only after admin presses Complete (meta.locked) — not mere COMPLETED from user receipt
    const isLocked = !!(order.meta?.locked || order.locked)
    if (isLocked) {
      return []
    }

    const remainingShip =
      order.total_remaining_to_ship_units ??
      (order.order_line_items || []).reduce(
        (s, li) =>
          s +
          (li.remaining_to_ship ??
            Math.max(0, (li.quantity || 0) - (li.shipped_quantity || 0))),
        0,
      )

    const firstShipmentAccepted = order.first_shipment_accepted === true

    const modifyAcceptAction = canModifyOrder
      ? [
          {
            id: 'modifyAccept',
            label: 'Modify Order',
            icon: EditIcon,
            color: 'warning',
          },
        ]
      : []

    const canCompleteOrder = hasAdminPermission(
      adminPermissions,
      'order',
      ADMIN_ACTIONS.COMPLETE_ORDER,
    )

    // Complete only after the grocer accepts the first shipment.
    const completeAction =
      canCompleteOrder && firstShipmentAccepted
        ? [
            {
              id: 'complete',
              label: 'Complete',
              icon: DoneAllIcon,
              color: 'success',
            },
          ]
        : []

    const shipAgainAction =
      remainingShip > 0
        ? [
            {
              id: 'createShipment',
              label: 'Fulfillment Center Assign',
              icon: LocalShippingIcon,
              color: 'success',
            },
          ]
        : []

    switch (status) {
      case 'CREATED':
        return [
          {
            id: 'accept',
            label: 'Accept',
            icon: CheckCircleIcon,
            color: 'success',
          },
          { id: 'reject', label: 'Reject', icon: CancelIcon, color: 'error' },
          ...modifyAcceptAction,
        ]
      case 'ACCEPTED': {
        const paymentSatisfied = isOrderPaymentSatisfiedForProcurement(order)
        const zeroPayable = isZeroPayableOrder(order)
        const canStartProcurement = hasAdminPermission(
          adminPermissions,
          'order',
          'startProcurement',
        )
        const paymentProofRequired = !zeroPayable
        const procurementActions = canStartProcurement
          ? [
              {
                id: 'startProcurement',
                label: 'Start Procurement',
                icon: PlayArrowIcon,
                color: 'primary',
                disabled: paymentProofRequired && !paymentSatisfied,
                disabledReason:
                  paymentProofRequired && !paymentSatisfied
                    ? 'Upload payment proof in the Payment Proof section before starting procurement.'
                    : undefined,
              },
            ]
          : []
        return [...modifyAcceptAction, ...procurementActions, ...completeAction]
      }
      case 'PROCUREMENT_STARTED': {
        const awaitingFulfillmentWebhook = (order.shipments || []).some(
          (shipment) =>
            String(shipment?.status || '').toUpperCase() ===
            'FULFILLMENT_CENTER_ASSIGNED',
        )
        const assignAction = awaitingFulfillmentWebhook
          ? shipAgainAction
          : [
              {
                id: 'dispatch',
                label: 'Fulfillment Center Assign',
                icon: LocalShippingIcon,
                color: 'success',
              },
            ]
        return [...modifyAcceptAction, ...assignAction, ...completeAction]
      }
      case 'FULFILLMENT_CENTER_ASSIGNED':
        return [...modifyAcceptAction, ...shipAgainAction, ...completeAction]
      case 'READY_FOR_DISPATCH':
        return [
          ...modifyAcceptAction,
          {
            id: 'dispatch',
            label: 'Fulfillment Center Assign',
            icon: LocalShippingIcon,
            color: 'success',
          },
          ...completeAction,
        ]
      case 'DISPATCHED':
        return [...modifyAcceptAction, ...shipAgainAction, ...completeAction]
      case 'COMPLETED':
        // Grocer receipt may set COMPLETED without lock. Allow Complete to lock
        // (and trim undelivered) once a shipment was confirmed; Dispatch again if remaining.
        return [...shipAgainAction, ...completeAction]
      case 'RETURN_IN_PROGRESS':
        return [
          { id: 'rejectReturn', label: 'Reject Return', icon: CancelIcon, color: 'error' },
          { id: 'acceptReturn', label: 'Accept Return', icon: CheckCircleIcon, color: 'success' },
        ]
      default:
        return []
    }
  }

  const availableActions = getAvailableActions()

  // If no actions available, don't render the panel
  if (availableActions.length === 0) {
    return null
  }

  const handleActionClick = (actionId) => {
    setActionError(null)
    // Check if action is disabled
    const action = availableActions.find(a => a.id === actionId)
    if (action?.disabled) {
      return // Don't proceed if action is disabled
    }
    
    if (actionId === 'reject') {
      setRejectModalOpen(true)
    } else if (actionId === 'modifyAccept') {
      setModifyModalOpen(true)
    } else if (actionId === 'dispatch' || actionId === 'createShipment') {
      setShipmentModalOpen(true)
    } else if (actionId === 'complete') {
      setActionError(null)
      setCompleteDialogOpen(true)
    } else if (
      actionId === 'startProcurement' &&
      isZeroPayableOrder(order) &&
      !hasOrderPaymentProofUploaded(order)
    ) {
      setSkipPaymentProofDialogOpen(true)
    } else {
      setPendingAction(actionId)
      setConfirmDialogOpen(true)
    }
  }

  const handleRejectSuccess = async () => {
    setSuccessMessage('Order has been rejected successfully')
    if (onOrderUpdated) {
      await onOrderUpdated()
    }
  }

  const handleModifySuccess = async () => {
    setSuccessMessage('Order has been modified')
    if (onOrderUpdated) {
      await onOrderUpdated()
    }
  }

  const handleCompleteConfirm = async (reason) => {
    if (!order) return
    try {
      setActionLoading(true)
      setActionError(null)
      const response = await adminOrderService.completeOrder(order.order_id, {
        reason,
      })
      setCompleteDialogOpen(false)
      setSuccessMessage(response?.message || 'Order completed and locked')
      if (onOrderUpdated) {
        await onOrderUpdated()
      }
    } catch (err) {
      console.error('Error completing order:', err)
      const parsed = parseInsufficientStockAcceptError(err)
      setActionError(parsed.message)
    } finally {
      setActionLoading(false)
    }
  }

  const handleConfirmAction = async () => {
    if (!pendingAction || !order) return

    try {
      setActionLoading(true)
      setActionError(null)
      setStockShortageItems(null)

      let response
      switch (pendingAction) {
        case 'accept':
          response = await orderService.acceptOrder(order.order_id)
          break
        case 'startProcurement':
          response = await orderService.startProcurement(order.order_id)
          break
        case 'readyForDispatch':
          response = await orderService.readyForDispatch(order.order_id)
          break
        case 'dispatch':
        case 'createShipment':
          break
        case 'rejectReturn':
          response = await orderService.rejectReturn(order.order_id)
          break
        case 'acceptReturn':
          response = await orderService.acceptReturn(order.order_id)
          break
        default:
          throw new Error('Unknown action')
      }

      // Close dialog and refetch order
      setConfirmDialogOpen(false)
      setPendingAction(null)
      setSuccessMessage(response?.message || 'Action completed successfully')

      // Refetch order detail
      if (onOrderUpdated) {
        await onOrderUpdated()
      }
    } catch (err) {
      console.error('Error performing action:', err)
      const parsed = parseInsufficientStockAcceptError(err)
      setActionError(parsed.message)
      setStockShortageItems(parsed.items)
    } finally {
      setActionLoading(false)
    }
  }

  const handleShipmentSuccess = async () => {
    setSuccessMessage('Fulfillment center assigned')
    if (onOrderUpdated) {
      await onOrderUpdated()
    }
  }

  const handleCancelAction = () => {
    setConfirmDialogOpen(false)
    setPendingAction(null)
    setActionError(null)
    setStockShortageItems(null)
  }

  const handleCancelSkipPaymentProof = () => {
    setSkipPaymentProofDialogOpen(false)
    setActionError(null)
  }

  const handleConfirmSkipPaymentProof = async () => {
    if (!order) return

    try {
      setActionLoading(true)
      setActionError(null)
      const response = await orderService.startProcurement(order.order_id)
      setSkipPaymentProofDialogOpen(false)
      setSuccessMessage(response?.message || 'Procurement started successfully')
      if (onOrderUpdated) {
        await onOrderUpdated()
      }
    } catch (err) {
      console.error('Error starting procurement:', err)
      const parsed = parseInsufficientStockAcceptError(err)
      setActionError(parsed.message)
      setStockShortageItems(parsed.items)
    } finally {
      setActionLoading(false)
    }
  }

  const getActionMessage = (actionId) => {
    switch (actionId) {
      case 'accept':
        return 'Are you sure you want to accept this order?'
      case 'startProcurement':
        if (hasPaymentProofs) {
          return 'Are you sure you want to start procurement for this order? Please confirm that you have reviewed the uploaded payment proofs.'
        }
        return 'Are you sure you want to start procurement for this order?'
      case 'readyForDispatch':
        return 'Are you sure you want to mark this order as ready for dispatch?'
      case 'dispatch':
        return 'Are you sure you want to dispatch this order? This will deduct inventory.'
      case 'rejectReturn':
        return 'Are you sure you want to reject this return? The user will be able to restore their inventory once.'
      case 'acceptReturn':
        return 'Are you sure you want to accept this return? Inventory will be added to admin inventory.'
      default:
        return 'Are you sure you want to perform this action?'
    }
  }

  const getActionTitle = (actionId) => {
    const action = availableActions.find((a) => a.id === actionId)
    return action ? `Confirm ${action.label}` : 'Confirm Action'
  }

  const hasPaymentProofs = hasOrderPaymentProofUploaded(order)
  const paymentSatisfied = isOrderPaymentSatisfiedForProcurement(order)
  const zeroPayable = isZeroPayableOrder(order)
  const payableAmount = getOrderPayableAmount(order)
  const remainingPaymentAmount = Number(
    order?.payment_proof_detail?.summary?.remaining ?? lazyRemainingPayment,
  )

  return (
    <>
      <Card sx={{ mb: 3, p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Order Actions
        </Typography>
        {order?.order_status === 'ACCEPTED' && !paymentSatisfied && !zeroPayable && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#ffebee', color: '#c62828' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Payment proof has not been uploaded. Procurement cannot be started until payment proof is provided.
            </Typography>
          </Alert>
        )}
        {order?.order_status === 'ACCEPTED' && zeroPayable && !hasPaymentProofs && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No payment is required for this order — the payable amount is ৳
              {payableAmount.toFixed(2)} (fully covered by credit note). You may optionally upload a
              payment proof; procurement can start without it.
            </Typography>
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {availableActions.map((action) => {
            const Icon = action.icon
            return (
              <Button
                key={action.id}
                variant="contained"
                startIcon={<Icon />}
                color={action.color}
                title={action.disabledReason || ''}
                onClick={() => handleActionClick(action.id)}
                disabled={actionLoading || action.disabled}
                sx={{
                  minWidth: 150,
                  color: '#fff',
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(0, 0, 0, 0.12) !important',
                    color: 'rgba(0, 0, 0, 0.38) !important',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedSuccess': {
                    bgcolor: '#4CAF50',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#45a049',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12) !important',
                      color: 'rgba(0, 0, 0, 0.38) !important',
                      boxShadow: 'none',
                    },
                  },
                  '&.MuiButton-containedError': {
                    bgcolor: '#d32f2f',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#b71c1c',
                    },
                  },
                  '&.MuiButton-containedPrimary': {
                    bgcolor: '#e90000',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#1b5e20',
                    },
                  },
                  '&.MuiButton-containedWarning': {
                    bgcolor: '#FF9800',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#f57c00',
                    },
                  },
                }}
              >
                {action.label}
              </Button>
            )
          })}
        </Box>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          {pendingAction ? getActionTitle(pendingAction) : 'Confirm Action'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => { setActionError(null); setStockShortageItems(null) }} sx={{ mb: 2 }}>
              {actionError}
              {stockShortageItems?.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, color: '#666' }}>
                  {stockShortageItems.map((row, idx) => (
                    <Typography component="li" variant="body2" key={`${row.inventory_id || row.sku}-${idx}`} sx={{ color: '#666' }}>
                      {row.product_name || row.sku || 'Product'} ({row.sku || '—'}): available {row.available ?? 0}, requested {row.requested ?? 0}
                    </Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <Typography sx={{ mb: 2 }}>
            {pendingAction ? getActionMessage(pendingAction) : ''}
          </Typography>

          {pendingAction === 'startProcurement' && hasPaymentProofs && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Proofs Available:
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                This order has {order.payment_proof_file_count || 0} payment proof file(s) uploaded. 
                Please review them before starting procurement.
              </Typography>
              {Number.isFinite(remainingPaymentAmount) && remainingPaymentAmount > 0 ? (
                <Typography variant="body2" sx={{ mt: 1, color: '#9a3412', fontWeight: 600 }}>
                  Remaining payment amount: ৳{remainingPaymentAmount.toFixed(2)}
                </Typography>
              ) : null}
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button
            onClick={handleCancelAction}
            disabled={actionLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={actionLoading}
            color={pendingAction === 'accept' ? 'success' : 'primary'}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            sx={
              pendingAction === 'accept'
                ? {
                    bgcolor: '#4CAF50',
                    color: '#FFFFFF',
                    '&:hover': { bgcolor: '#45a049' },
                  }
                : {
                    bgcolor: '#e90000',
                    color: '#FFFFFF',
                    '&:hover': { bgcolor: '#c62828' },
                  }
            }
          >
            {actionLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zero-payable: skip payment proof before Start Procurement */}
      <Dialog
        open={skipPaymentProofDialogOpen}
        onClose={handleCancelSkipPaymentProof}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Skip Payment Proof?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }}>
            The payable amount is ৳{payableAmount.toFixed(2)}. Would you like to skip payment proof upload?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button
            onClick={handleCancelSkipPaymentProof}
            disabled={actionLoading}
            color="inherit"
          >
            No
          </Button>
          <Button
            onClick={handleConfirmSkipPaymentProof}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            sx={{
              bgcolor: '#e90000',
              color: '#FFFFFF',
              '&:hover': { bgcolor: '#c62828' },
            }}
          >
            {actionLoading ? 'Processing...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Order Modal */}
      <RejectOrderModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        orderId={order?.order_id}
        onSuccess={handleRejectSuccess}
      />

      {canModifyOrder && (
        <ModifyAcceptOrderModal
          open={modifyModalOpen}
          onClose={() => setModifyModalOpen(false)}
          order={order}
          onSuccess={handleModifySuccess}
        />
      )}

      <CreateShipmentModal
        open={shipmentModalOpen}
        onClose={() => setShipmentModalOpen(false)}
        order={order}
        title="Fulfillment Center Assign"
        canEnterManualSo={canEnterManualSo}
        externalIntegrationEnabled={externalIntegrationEnabled}
        onSuccess={handleShipmentSuccess}
        onProductDsdUpdated={onProductDsdUpdated}
        onProductVatUpdated={onProductVatUpdated}
      />

      <CompleteOrderDialog
        open={completeDialogOpen}
        onClose={() => {
          if (!actionLoading) {
            setCompleteDialogOpen(false)
            setActionError(null)
          }
        }}
        onConfirm={handleCompleteConfirm}
        loading={actionLoading}
        error={actionError}
        onClearError={() => setActionError(null)}
        isPartialDelivery={wouldCompleteAsPartialDelivery(order)}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  )
}

export default OrderActionsPanel
